// Fetch client for the CRM BFF (crm/apps/api, served under /api/v1).
//
// This is deliberately SEPARATE from src/api/http.ts (the admin-api client):
//   - The CRM authenticates with an httpOnly session cookie (JWT HS256, Google
//     SSO), NOT the Lexik RS256 Bearer token that http.ts carries. So every
//     request here uses `credentials: 'include'` and sends NO Authorization
//     header — the browser attaches the cross-subdomain cookie itself
//     (Domain=.life5.com, set by the CRM API).
//   - A 401 from the CRM means "not connected to the CRM yet", which is a
//     different failure mode from an expired admin session. It has its own
//     global handler so CrmSessionContext can flip `connected` to false and
//     surface a "Conectar con CRM" affordance, without touching AuthContext.

const BASE_URL =
  process.env.REACT_APP_CRM_API_URL?.replace(/\/$/, "") ??
  "http://localhost:4000/api/v1";

export class CrmHttpError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`CRM HTTP ${status}`);
  }
}

type Options = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
};

/**
 * Module-level slot for a "CRM session invalid" listener. CrmSessionProvider
 * installs it on mount; on a 401 from any CRM endpoint crmRequest() invokes it
 * so the SPA can mark itself disconnected from the CRM and prompt the user to
 * connect, without every caller duplicating that logic.
 *
 * Singular by design, mirroring http.ts: the SPA has a single CRM session, so a
 * single handler keeps the contract trivial — repeated 401s from concurrent
 * requests just mark disconnected multiple times, which is idempotent.
 */
let crmUnauthorizedHandler: (() => void) | null = null;

export function setCrmUnauthorizedHandler(handler: (() => void) | null): void {
  crmUnauthorizedHandler = handler;
}

/**
 * Human-readable one-liner for a CRM HTTP error, safe to surface in the UI.
 * Extracted here (from LeadTickets) so any CRM panel can reuse the same
 * "HTTP {status}: {detail}" rendering of the BFF error body.
 */
export function describeCrmError(err: CrmHttpError): string {
  const body = err.body as { message?: string; error?: string } | undefined;
  const detail = (body?.message ?? body?.error ?? "").slice(0, 240);
  return `HTTP ${err.status}: ${detail || "Error inesperado del CRM"}`;
}

/**
 * Descarga binaria autenticada desde el CRM. Los adjuntos de nota de ticket (y
 * cualquier otro blob privado) se sirven como stream con Content-Disposition
 * desde el BFF — no JSON —, así que crmRequest (que parsea la respuesta) no
 * sirve. Aquí hacemos un fetch crudo con la cookie de sesión (credentials:
 * 'include', SIN Authorization ni Content-Type manual), traemos el blob y
 * disparamos la descarga con el nombre real del archivo.
 *
 * Un 401 dispara el mismo handler global que crmRequest (marca desconectado);
 * cualquier otro error !ok se propaga como CrmHttpError para que el llamante
 * pueda describirlo con describeCrmError.
 */
export async function crmDownload(
  path: string,
  filename: string,
): Promise<void> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 401) crmUnauthorizedHandler?.();
    // El cuerpo de error del BFF es JSON; intentamos parsearlo para el detalle.
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json().catch(() => undefined)
      : await response.text().catch(() => undefined);
    throw new CrmHttpError(response.status, payload);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Igual que crmDownload pero, en vez de disparar la descarga, devuelve un
 * objectURL del blob (para usar como `src` de un <img> inline). El llamante es
 * responsable de revocarlo (URL.revokeObjectURL) al desmontar. Misma auth por
 * cookie (credentials: 'include', cross-origin desde Operations) y mismo manejo
 * de 401 (handler global) y de errores (CrmHttpError) que crmDownload.
 */
export async function crmFetchBlobUrl(path: string): Promise<string> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 401) crmUnauthorizedHandler?.();
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json().catch(() => undefined)
      : await response.text().catch(() => undefined);
    throw new CrmHttpError(response.status, payload);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export async function crmRequest<T>(
  path: string,
  opts: Options = {},
): Promise<T> {
  // Content-Type SOLO cuando hay body: un request sin body (GET/DELETE, o los
  // POST de restore) con `application/json` hace que el parser de Fastify
  // intente leer un body JSON vacío y lo rechace (400 FST_ERR_CTP_EMPTY_JSON_
  // BODY) ANTES de la autorización. El propio cliente del CRM añade el header
  // condicionalmente por este mismo caso real (apps/web/src/shared/api/client.ts).
  const hasBody = opts.body !== undefined;
  const headers: Record<string, string> = hasBody
    ? { "Content-Type": "application/json" }
    : {};

  const response = await fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    // Send the CRM session cookie on every request (cross-subdomain).
    credentials: "include",
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    if (response.status === 401) {
      // Fire-and-forget: the handler is expected to be cheap (flip a flag) and
      // the throw below still happens so callers' error branches run normally.
      crmUnauthorizedHandler?.();
    }
    throw new CrmHttpError(response.status, payload);
  }

  return payload as T;
}
